interface IconProps {
  size?: number;
}

const base = (size: number): React.SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: "0 0 24 24",
  fill: "none",
  stroke: "currentColor",
  strokeWidth: 1.6,
  strokeLinecap: "round" as const,
  strokeLinejoin: "round" as const,
  "aria-hidden": true,
  focusable: false,
});

export function CarveIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M15.5 3.5 20.5 8.5 11 18 6 13z" />
      <path d="M6 13 3.5 20.5 11 18" />
    </svg>
  );
}

export function SmoothIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M3 15c3 0 3-5 6-5s3 5 6 5 3-5 6-5" />
      <path d="M3 19.5c3 0 3-3 6-3s3 3 6 3 3-3 6-3" opacity="0.5" />
    </svg>
  );
}

export function CutIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M6.5 3.5 14 14.2M17.5 3.5 10 14.2" />
      <circle cx="7.6" cy="17.8" r="2.8" />
      <circle cx="16.4" cy="17.8" r="2.8" />
    </svg>
  );
}

export function AngleIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M3.5 19.5h17M4.5 19.5 18.5 5.5" />
      <path d="M9.5 19.5a5 5 0 0 0-1.5-3.6" opacity="0.7" />
      <path d="m15.5 7.5 3-2 2 3" opacity="0.55" />
    </svg>
  );
}

export function FlangeIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M3 18.5h18" />
      <path d="M5 17.5c2.5 0 3.3-5 6.7-5h3.8c1.8 0 2.3 2.1 3.5 3.2" />
      <path d="M5 15.2v2.3M19 15.2v2.3" opacity="0.6" />
    </svg>
  );
}

export function LockIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <rect x="5.5" y="10" width="13" height="10" rx="1.5" />
      <path d="M8.5 10V7.5a3.5 3.5 0 0 1 7 0V10" />
      <path d="M12 14v2.5" />
    </svg>
  );
}

export function MenuIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M4 6.5h16M4 12h16M4 17.5h16" />
    </svg>
  );
}

export function TextureIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="m14.8 3.8 5.4 5.4-8.6 8.6-5.4-5.4z" />
      <path d="m6.2 12.4-2.4 6.8 6.8-2.4" />
      <path d="M16.8 6.2 18 5M5 20h14" opacity="0.6" />
    </svg>
  );
}

export function EditModeIcon({ size = 20 }: IconProps): React.ReactElement {
  return <CarveIcon size={size} />;
}

export function OrbitIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <circle cx="12" cy="12" r="3" />
      <path d="M4 12a8 4.5 0 0 0 14.5 2.6M20 12A8 4.5 0 0 0 5.5 9.4" />
      <path d="m17.7 16.8 1-2.5-2.7-.3" />
    </svg>
  );
}

export function PanIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M12 3v18M3 12h18" />
      <path d="m9 6 3-3 3 3M9 18l3 3 3-3M6 9l-3 3 3 3M18 9l3 3-3 3" />
    </svg>
  );
}

export function ZoomIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <circle cx="10.5" cy="10.5" r="6.5" />
      <path d="m15.3 15.3 5 5M7.5 10.5h6M10.5 7.5v6" />
    </svg>
  );
}

export function FixingIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M7.5 5.5h9l-2.4 3.2v10.1L12 21.5l-2.1-2.7V8.7z" />
      <path d="M9.9 11.5h4.2M9.9 14.5h4.2" opacity="0.6" />
    </svg>
  );
}

export function UndoIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M4 9h10a5 5 0 0 1 0 10h-3" />
      <path d="m7.5 5.5-3.5 3.5 3.5 3.5" />
    </svg>
  );
}

export function RedoIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M20 9H10a5 5 0 0 0 0 10h3" />
      <path d="m16.5 5.5 3.5 3.5-3.5 3.5" />
    </svg>
  );
}

export function ResetIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M20 12a8 8 0 1 1-2.5-5.8" />
      <path d="M20 4v4.5h-4.5" />
    </svg>
  );
}

export function NewIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M5 3.5h10l4 4v13H5z" />
      <path d="M15 3.5v4h4M12 11v6M9 14h6" />
    </svg>
  );
}

export function SaveIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M5 3.5h12l2 2v15H5z" />
      <path d="M8 3.5v6h8v-6M8 20.5v-7h8v7" />
    </svg>
  );
}

export function ExportIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M12 3.5v11" />
      <path d="m7.5 10 4.5 4.5 4.5-4.5" />
      <path d="M4.5 17.5v2a1 1 0 0 0 1 1h13a1 1 0 0 0 1-1v-2" />
    </svg>
  );
}

export function HelpIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M9.7 9.5a2.3 2.3 0 1 1 2.9 2.2c-.5.2-.6.5-.6 1v.6" />
      <path d="M12 16.6h.01" />
    </svg>
  );
}

export function SoundOnIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M5 10h3l4-3.5v11L8 14H5z" />
      <path d="M15 9.2a4 4 0 0 1 0 5.6M17.7 6.6a7.5 7.5 0 0 1 0 10.8" />
    </svg>
  );
}

export function SoundOffIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M5 10h3l4-3.5v11L8 14H5z" />
      <path d="m16 10 4 4M20 10l-4 4" />
    </svg>
  );
}

export function TrashIcon({ size = 20 }: IconProps): React.ReactElement {
  return (
    <svg {...base(size)}>
      <path d="M4.5 6.5h15" />
      <path d="M9.5 6.5V4.8a1 1 0 0 1 1-1h3a1 1 0 0 1 1 1v1.7" />
      <path d="M6.5 6.5 7.4 20a1 1 0 0 0 1 .9h7.2a1 1 0 0 0 1-.9l.9-13.5" />
    </svg>
  );
}

/** Simple axonometric block used on the setup screen. */
export function BlockFigure({
  width,
  depth,
  height,
  scale,
}: {
  width: number;
  depth: number;
  height: number;
  scale: number;
}): React.ReactElement {
  const w = width * scale;
  const d = depth * scale * 0.5;
  const h = height * scale;
  const cx = 80;
  const cy = 88;

  const top = `${cx},${cy - h - d} ${cx + w / 2},${cy - h - d / 2} ${cx},${cy - h} ${cx - w / 2},${cy - h - d / 2}`;
  const left = `${cx - w / 2},${cy - h - d / 2} ${cx},${cy - h} ${cx},${cy} ${cx - w / 2},${cy - d / 2}`;
  const right = `${cx + w / 2},${cy - h - d / 2} ${cx},${cy - h} ${cx},${cy} ${cx + w / 2},${cy - d / 2}`;

  return (
    <svg width="160" height="128" viewBox="0 0 160 128" aria-hidden focusable="false">
      <polygon points={top} fill="#d8c5a3" fillOpacity="0.96" />
      <polygon points={left} fill="#d8c5a3" fillOpacity="0.68" />
      <polygon points={right} fill="#d8c5a3" fillOpacity="0.46" />
      <polygon
        points={`${cx - w / 2},${cy - d / 2} ${cx},${cy} ${cx + w / 2},${cy - d / 2} ${cx},${cy - d}`}
        fill="none"
        stroke="#c8d7bc"
        strokeOpacity="0.5"
        strokeWidth="1"
        strokeDasharray="3 3"
      />
    </svg>
  );
}
